/**
 * CAPABILITY-GATE-ERADICATION-1 · CapabilityService
 *
 * Single source of truth for tenant capability + module gating on the client.
 *
 * Two paths, deliberately separate:
 *
 *   1. Module enablement (licensing) — read from
 *        tenants/{tenantId}.config.modules.enabled.<moduleId>  (preferred)
 *      with a backward-compat fallback to the legacy MODULES-1 array
 *        tenants/{tenantId}.enabled_modules[]
 *      Use isModuleEnabled / useModuleEnabled. NEVER use useCapability for a
 *      module-enable check — that anti-pattern is what
 *      CAPABILITY-GATE-ERADICATION-1 was opened to fix.
 *
 *   2. Fine-grained feature flags — read from
 *        tenants/{tenantId}/capabilities/current.flags['<ns>.<feature>']
 *      Use isCapabilityEnabled / useCapabilityEnabled. Namespace is validated
 *      against FROZEN_NAMESPACES at every call. Boot throws if the frozen
 *      list ever shrinks below the canonical 12.
 *
 * Both reads go through DataService — never direct firebase/firestore.
 * Result is cached for 60 s per tenant; clearCache() invalidates.
 */

import DataService from './DataService.js';
import { useEffect, useState } from 'react';
import { hasCapability as legacyHasCapability } from '../platform/hasCapability';

/**
 * Frozen capability namespaces (CAP-TAX-1, post-CAPABILITY-GATE-ERADICATION-1).
 * Adding or removing requires a new CAP-TAX-N session.
 *
 *   cx, comms, kpol, kfol, kiol, exec, aegis, sentinel,
 *   sandbox, idp, integrations, counsel,
 *   embedding, forecast, kbrain   ← CAP-TAX-3 (EMBED-1, 2026-04-29)
 *   customer                      ← PILLAR-KCX-1 (2026-04-30)
 *   kfin                          ← PILLAR-KFIN-1 (2026-04-30)
 *   ksales-ops                    ← PILLAR-KSALES-OPS-1 (2026-05-01)
 *   kstrat                        ← PILLAR-KSTRAT-1 (2026-05-01)
 *
 * `platform` was retired in CAPABILITY-GATE-ERADICATION-1. `counsel` was admitted.
 * `embedding`, `forecast`, `kbrain` admitted in CAP-TAX-3 (Wave KB-A).
 * `customer` admitted in PILLAR-KCX-1 (2026-04-30) — Customer Experience Pillar.
 * `kfin` admitted in PILLAR-KFIN-1 (2026-04-30) — Financial Pillar substrate.
 * `ksales-ops` admitted in PILLAR-KSALES-OPS-1 (2026-05-01) — Sales Operations Pillar R3.
 * `kstrat` admitted in PILLAR-KSTRAT-1 (2026-05-01) — Strategy Pillar R3.
 */
export const FROZEN_NAMESPACES = Object.freeze([
  'cx',
  'comms',
  'kpol',
  'kfol',
  'kiol',
  'exec',
  'aegis',
  'sentinel',
  'sandbox',
  'idp',
  'integrations',
  'counsel',
  // CAP-TAX-3 (EMBED-1, 2026-04-29) — Wave KB-A substrate namespaces
  'embedding',  // Voyage-3 embedding substrate (EMBED-1)
  'forecast',   // Forecast substrate (TS-1, pending)
  'kbrain',     // KBRAIN observation/audit substrate (LOG-1, pending)
  // PILLAR-KCX-1 (2026-04-30) — Customer Experience Pillar
  'customer',
  // PILLAR-KFIN-1 (2026-04-30) — Financial Pillar substrate
  'kfin',
  // PILLAR-KSALES-OPS-1 (2026-05-01) — Sales Operations Pillar R3
  'ksales-ops',
  // PILLAR-KSTRAT-1 (2026-05-01) — Strategy Pillar R3
  'kstrat',
]);

const FROZEN_SET = new Set(FROZEN_NAMESPACES);

// Boot-time integrity check — count updated to 19 by PILLAR-KSALES-OPS-1 + PILLAR-KSTRAT-1 (2026-05-01).
if (FROZEN_NAMESPACES.length !== 19) {
  throw new Error(
    `[CapabilityService] FROZEN_NAMESPACES must contain exactly 19 entries; ` +
    `got ${FROZEN_NAMESPACES.length}. The taxonomy is governed by CAP-TAX-N.`,
  );
}

function assertFrozenNamespace(namespace) {
  if (typeof namespace !== 'string' || !FROZEN_SET.has(namespace)) {
    throw new Error(
      `[CapabilityService] "${namespace}" is not a frozen capability namespace. ` +
      `Allowed: ${FROZEN_NAMESPACES.join(', ')}.`,
    );
  }
}

const CACHE_TTL_MS = 60_000;
const tenantCache = new Map(); // tenantId → { ts, tenant }

async function loadTenant(tenantId) {
  if (!tenantId) return null;
  const cached = tenantCache.get(tenantId);
  if (cached && Date.now() - cached.ts < CACHE_TTL_MS) return cached.tenant;
  const tenant = await DataService.getTenant(tenantId);
  tenantCache.set(tenantId, { ts: Date.now(), tenant });
  return tenant;
}

export function clearCapabilityServiceCache(tenantId) {
  if (tenantId) tenantCache.delete(tenantId);
  else tenantCache.clear();
}

function readModuleFlag(tenant, moduleId) {
  if (!tenant || !moduleId) return false;
  const id = String(moduleId);
  const lower = id.toLowerCase();
  const upper = id.toUpperCase();

  // Canonical path — config.modules.enabled.<moduleId>
  const enabledMap = tenant?.config?.modules?.enabled;
  if (enabledMap && typeof enabledMap === 'object') {
    if (enabledMap[id] === true) return true;
    if (enabledMap[lower] === true) return true;
    if (enabledMap[upper] === true) return true;
  }

  // Backward-compat — MODULES-1 enabled_modules[] array on the tenant doc
  const arr = Array.isArray(tenant?.enabled_modules) ? tenant.enabled_modules : null;
  if (arr) {
    if (arr.includes(upper)) return true;
    if (arr.includes(id)) return true;
  }

  return false;
}

/**
 * Returns true if the given module is enabled for the tenant.
 *
 * Reads `tenants/{tenantId}.config.modules.enabled.<moduleId>` first;
 * falls back to the legacy MODULES-1 `enabled_modules[]` array. Never reads
 * `config.platform.<module>_active` (the deprecated path that triggered
 * CAPABILITY-GATE-ERADICATION-1).
 *
 * Fail-closed: missing tenant doc / missing config returns false.
 */
export async function isModuleEnabled(tenantId, moduleId) {
  if (!tenantId || !moduleId) return false;
  const tenant = await loadTenant(tenantId);
  return readModuleFlag(tenant, moduleId);
}

/**
 * Returns true if `<namespace>.<capability>` is enabled for the tenant.
 *
 * `namespace` MUST be one of FROZEN_NAMESPACES; otherwise this throws.
 * Reads through the legacy `hasCapability` adapter so existing
 * `tenants/{tid}/capabilities/current` documents continue to be honoured
 * for fine-grained feature flags.
 */
export async function isCapabilityEnabled(tenantId, namespace, capability) {
  assertFrozenNamespace(namespace);
  if (!tenantId || !capability) return false;
  const flag = `${namespace}.${capability}`;
  return legacyHasCapability(tenantId, flag);
}

let _resolveTenantId = () => null;

/**
 * Inject a tenantId resolver at app bootstrap. Hooks fall back to this when
 * no explicit tenantId is passed.
 */
export function configureTenantResolver(resolver) {
  if (typeof resolver !== 'function') return;
  _resolveTenantId = resolver;
}

/**
 * React hook — returns { enabled, loading } for a module gate.
 *
 *   const { enabled, loading } = useModuleEnabled('TMS');
 */
export function useModuleEnabled(moduleId, explicitTenantId) {
  const [state, setState] = useState({ enabled: false, loading: true });

  useEffect(() => {
    let active = true;
    const tid = explicitTenantId ?? _resolveTenantId() ?? '';
    if (!tid || !moduleId) {
      setState({ enabled: false, loading: false });
      return () => { active = false; };
    }
    isModuleEnabled(tid, moduleId)
      .then(enabled => { if (active) setState({ enabled, loading: false }); })
      .catch(() => { if (active) setState({ enabled: false, loading: false }); });
    return () => { active = false; };
  }, [moduleId, explicitTenantId]);

  return state;
}

/**
 * React hook — returns { enabled, loading } for a fine-grained capability.
 *
 *   const { enabled, loading } = useCapabilityEnabled('aegis', 'uco_reference_impl');
 */
export function useCapabilityEnabled(namespace, capability, explicitTenantId) {
  // Validate at hook level as the spec requires — throws synchronously so a
  // disallowed namespace surfaces in the React render path, not silently.
  assertFrozenNamespace(namespace);

  const [state, setState] = useState({ enabled: false, loading: true });

  useEffect(() => {
    let active = true;
    const tid = explicitTenantId ?? _resolveTenantId() ?? '';
    if (!tid || !capability) {
      setState({ enabled: false, loading: false });
      return () => { active = false; };
    }
    isCapabilityEnabled(tid, namespace, capability)
      .then(enabled => { if (active) setState({ enabled, loading: false }); })
      .catch(() => { if (active) setState({ enabled: false, loading: false }); });
    return () => { active = false; };
  }, [namespace, capability, explicitTenantId]);

  return state;
}

const CapabilityService = {
  FROZEN_NAMESPACES,
  isModuleEnabled,
  isCapabilityEnabled,
  useModuleEnabled,
  useCapabilityEnabled,
  configureTenantResolver,
  clearCapabilityServiceCache,
};

export default CapabilityService;
